import React, { useState } from 'react';
import Icon from '../../components/AppIcon';
import WorkshopFullScreenSheet from './WorkshopFullScreenSheet';
import WorkshopProductionSheetContent from './WorkshopProductionSheetContent';
import WorkshopStrippingSheetContent from './WorkshopStrippingSheetContent';

/** Hauteur réservée pour le contenu scrollable (barre fixe + safe-area). */
export const WORKSHOP_DOCUMENT_BAR_HEIGHT = '5.25rem';

function openProductionSheetPage({ caseId, batchId }) {
  const params = new URLSearchParams();
  if (caseId) params.set('caseId', String(caseId));
  if (batchId) params.set('batchId', String(batchId));
  window.open(`/production-sheet?${params.toString()}`, '_blank');
}

function openIprSheetPage({ caseId }) {
  if (!caseId) return;
  const params = new URLSearchParams({ caseId: String(caseId) });
  window.open(`/ipr-stripping-sheet?${params.toString()}`, '_blank');
}

/**
 * Barre fixe bas d’écran : fiche production + fiche stripping (plein écran).
 */
export default function WorkshopDocumentBar({
  batch,
  caseId,
  iprData,
  iprLoading,
  canEditProduction = true,
}) {
  const [productionOpen, setProductionOpen] = useState(false);
  const [strippingOpen, setStrippingOpen] = useState(false);

  const subtitle = batch
    ? `${batch.batchNumber || 'Lot'} · ${batch.patientName || 'Patient'}`
    : '';

  const sheetOpen = productionOpen || strippingOpen;

  return (
    <>
      <nav
        className={`fixed bottom-0 left-0 right-0 z-30 border-t border-border bg-card shadow-[0_-4px_24px_rgba(0,0,0,0.08)] pb-[max(0.5rem,env(safe-area-inset-bottom))] transition-opacity ${
          sheetOpen ? 'opacity-0 pointer-events-none' : 'opacity-100'
        }`}
        aria-label="Fiches référentielles"
        aria-hidden={sheetOpen}
      >
        <div className="max-w-lg mx-auto flex gap-2 px-3 pt-2">
          <button
            type="button"
            onClick={() => setProductionOpen(true)}
            className="flex-1 min-h-[52px] flex items-center justify-center gap-2 rounded-xl border border-primary/30 bg-primary/10 text-primary font-semibold text-sm hover:bg-primary/15 cursor-pointer transition-colors"
            aria-label="Ouvrir la fiche de production"
          >
            <Icon name="FileText" size={20} />
            <span>Fiche production</span>
          </button>
          <button
            type="button"
            onClick={() => setStrippingOpen(true)}
            disabled={!caseId}
            className="flex-1 min-h-[52px] flex items-center justify-center gap-2 rounded-xl border border-border bg-muted/40 text-foreground font-semibold text-sm hover:bg-muted/60 disabled:opacity-45 disabled:cursor-not-allowed cursor-pointer transition-colors"
            aria-label="Ouvrir la fiche de stripping"
          >
            <Icon name="Layers" size={20} />
            <span>Fiche stripping</span>
          </button>
        </div>
      </nav>

      <WorkshopFullScreenSheet
        open={productionOpen}
        onClose={() => setProductionOpen(false)}
        title="Fiche de production"
        subtitle={subtitle}
        footer={
          canEditProduction ? (
            <button
              type="button"
              onClick={() => openProductionSheetPage({ caseId, batchId: batch?.id })}
              className="w-full min-h-[52px] rounded-xl bg-primary text-primary-foreground font-semibold text-sm hover:bg-primary/90 cursor-pointer flex items-center justify-center gap-2"
            >
              <Icon name="Pencil" size={18} />
              Modifier la fiche
            </button>
          ) : null
        }
      >
        <WorkshopProductionSheetContent batch={batch} caseId={caseId} />
      </WorkshopFullScreenSheet>

      <WorkshopFullScreenSheet
        open={strippingOpen}
        onClose={() => setStrippingOpen(false)}
        title="Fiche stripping (IPR)"
        subtitle={subtitle}
        footer={
          caseId ? (
            <button
              type="button"
              onClick={() => openIprSheetPage({ caseId })}
              className="w-full min-h-[52px] rounded-xl bg-primary text-primary-foreground font-semibold text-sm hover:bg-primary/90 cursor-pointer flex items-center justify-center gap-2"
            >
              <Icon name="Pencil" size={18} />
              Modifier la fiche
            </button>
          ) : null
        }
      >
        <WorkshopStrippingSheetContent caseId={caseId} iprData={iprData} iprLoading={iprLoading} />
      </WorkshopFullScreenSheet>
    </>
  );
}
